/**
 * Gate R2 notification policy — mirrors SQL ax1_notification_policy_capability.
 * Used by UI labels/tests; authoritative fanout remains in Postgres.
 */

export type NotificationChannel = 'in_app' | 'email' | 'webhook';
export type NotificationDeliveryStatus = 'pending' | 'delivered' | 'failed' | 'skipped';

export const SECURITY_NOTIFICATION_EVENT_TYPES = [
  'security_incident_opened',
  'security_incident_escalated',
  'incident_opened',
  'incident_escalated',
  'jit_created',
  'jit_approved',
  'jit_rejected',
  'jit_expired',
  'audit_export',
  'high_risk_export',
  'api_key_created',
  'api_key_rotated',
  'api_key_revoked',
  'permission_change',
  'role_change',
  'access_assigned',
  'access_revoked',
  'explicit_right.assigned',
  'explicit_right.granted',
  'report_assigned',
  'review_due',
  'integration_failed',
  'security_claim_expiring',
  'suspicious_activity',
  'suspicious_login',
  'suspended_user_login_attempt',
  'access_denied',
] as const;

export type SecurityNotificationEventType =
  (typeof SECURITY_NOTIFICATION_EVENT_TYPES)[number];

export function notificationPolicyCapability(
  eventType: string,
  severity: string = 'info',
  details: Record<string, unknown> = {},
): string | null {
  const type = (eventType || '').toLowerCase();
  const sev = (severity || 'info').toLowerCase();
  const source = String(details.source ?? '');

  if (
    type === 'security_incident_opened' ||
    type === 'security_incident_escalated' ||
    type === 'incident_opened' ||
    type === 'incident_escalated' ||
    type === 'incident_created' ||
    type.startsWith('security_incident')
  ) {
    return 'security.incidents.view';
  }

  if (
    type.startsWith('jit_') ||
    [
      'privileged_access_requested',
      'privileged_access_approved',
      'privileged_access_rejected',
      'privileged_access_expired',
    ].includes(type) ||
    source === 'platform_jit'
  ) {
    return 'security.privileged_access.approve';
  }

  if (
    ['audit_export', 'high_risk_export', 'data_export_high_risk'].includes(type) &&
    ['high', 'critical', 'warning'].includes(sev)
  ) {
    return 'security.audit.view';
  }

  if (['api_key_created', 'api_key_rotated', 'api_key_revoked'].includes(type)) {
    return 'security.api_keys.view';
  }

  if (
    [
      'permission_change',
      'role_change',
      'membership_status_change',
      'user_suspended',
      'user_reactivated',
      'user_deletion',
      'access_assigned',
      'access_revoked',
      'explicit_right.assigned',
      'explicit_right.granted',
      'project_participant_added',
      'project_participant_removed',
      'workspace_member_added',
      'workspace_member_removed',
      'company_member_added',
      'company_member_removed',
    ].includes(type)
  ) {
    return 'security.identity.view';
  }

  if (
    ['report_assigned', 'report_generated', 'report_submitted', 'review_due', 'review_assigned'].includes(
      type,
    )
  ) {
    return 'security.assurance.view';
  }

  if (
    ['integration_failed', 'integration_error', 'connected_service_error'].includes(type) ||
    (type.startsWith('integration_') && ['high', 'critical', 'warning'].includes(sev))
  ) {
    return 'security.integrations.view';
  }

  if (['security_claim_expiring', 'claim_expiring', 'claim_expiry_warning'].includes(type)) {
    return 'security.compliance.view';
  }

  if (
    [
      'suspicious_activity',
      'suspicious_login',
      'login_failed',
      'suspended_user_login_attempt',
      'access_denied',
    ].includes(type)
  ) {
    return 'security.audit.view';
  }

  return null;
}

export function isSecurityDerivedNotificationType(type: string): boolean {
  return notificationPolicyCapability(type) != null || type.startsWith('security_');
}
